import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import DDZWalletEntry from "./index";
import { PersistContext } from "@/data/PersistProvider";
import { LanguageContext } from "@/data/LanguageProvider";
import { useEnhancedHistory } from "@/data/RouterProvider";
import { ChannelRemind } from "@/utils/UrlHelper";
import { GetHallServerUrl } from "@/data/urls";
import { DDZ_INVITE_PATH, inviteFromSearch, inviteLocation } from "@/utils/DDZInvite";

// The local Pakistan test server routes all partner games to DDZ.
// Production builds only intercept the real DDZ game UID.
export const isDDZEntry = game => !!game && (game.game_uid === "fz_ddz_lobby" ||
  (process.env.NODE_ENV !== "production" && window.location.port === "8076"));

const EntryContext = createContext(null);
const pendingKey = () => `ddz_invite_pending:${encodeURIComponent(GetHallServerUrl())}`;
function storedInvite(value) {
  try {
    if (value === undefined) return sessionStorage.getItem(pendingKey());
    if (value === null) sessionStorage.removeItem(pendingKey());
    else sessionStorage.setItem(pendingKey(), value);
  } catch (_) { /* The login return URL also preserves the invitation. */ }
  return null;
}

export function DDZWalletProvider({ children }) {
  const { getPersist, setPersist, reloadUserInfo } = useContext(PersistContext);
  const { getLanguage } = useContext(LanguageContext);
  const history = useEnhancedHistory();
  const location = useLocation();
  const loaded = getPersist("dataLoaded");
  const user = getPersist("user_info");
  const token = user && !user.is_guest ? user.token : null;
  const account = user && user.rid;
  const [entry, setEntry] = useState(null);
  const revision = useRef(0);
  const handled = useRef("");
  const actions = useRef({ history, setPersist, reloadUserInfo });
  actions.current = { history, setPersist, reloadUserInfo };
  const pending = getPersist("ddz_wallet_open");
  const returnKey = `ddz_wallet_return:${encodeURIComponent(GetHallServerUrl())}:${account}`;

  useEffect(() => {
    if (!loaded) return;
    const { history, setPersist } = actions.current;
    let inviteToken = inviteFromSearch(location.search);
    const authPage = /^\/(login|sendRegCode)$/.test(location.pathname);
    if (inviteToken === null && location.pathname === DDZ_INVITE_PATH) inviteToken = storedInvite() || "";
    if (inviteToken === null && authPage && !token) {
      const saved = storedInvite();
      if (saved !== null) setPersist("login_back_url", ChannelRemind(inviteLocation(saved)));
      return;
    }
    if (inviteToken !== null) {
      storedInvite(inviteToken);
      setPersist("ddz_wallet_open", false);
      if (!token) {
        setEntry(null);
        setPersist("login_back_url", ChannelRemind(inviteLocation(inviteToken)));
        if (!authPage) history.replace(ChannelRemind("/login"));
        else if (inviteFromSearch(location.search) !== null) {
          const query = new URLSearchParams(location.search);
          query.delete("invite_token");
          history.replace(`${location.pathname}${query.toString() ? `?${query}` : ""}`);
        }
        return;
      }
      if (authPage) {
        history.replace(ChannelRemind(inviteLocation(inviteToken)));
        return;
      }
      const key = `${token}:${location.key}:${location.pathname}:${location.search}`;
      if (handled.current === key) return;
      handled.current = key;
      setEntry({ token, inviteToken, revision: ++revision.current });
    } else if (token && pending && !authPage) {
      setPersist("ddz_wallet_open", false);
      setEntry({ token, inviteToken: null, revision: ++revision.current });
    }
  }, [loaded, token, pending, location.key, location.pathname, location.search]);

  useEffect(() => {
    const restore = () => {
      if (!token) return;
      try {
        const saved = sessionStorage.getItem(returnKey);
        if (saved === null) return;
        sessionStorage.removeItem(returnKey);
        if (/^\/(login|sendRegCode)$/.test(window.location.pathname)) return;
        let inviteToken = null;
        if (saved !== "1") {
          try { inviteToken = JSON.parse(saved).inviteToken; } catch (_) { return; }
          if (typeof inviteToken !== "string") inviteToken = null;
        }
        const latestInvite = inviteFromSearch(window.location.search);
        if (latestInvite !== null && latestInvite !== inviteToken) return;
        // Returning players must be able to transfer out even if their invitation has expired.
        storedInvite(null);
        setEntry({ token, inviteToken: null, revision: ++revision.current });
      } catch (_) { return; }
      actions.current.reloadUserInfo(token);
    };
    restore();
    window.addEventListener("pageshow", restore);
    return () => window.removeEventListener("pageshow", restore);
  }, [token, returnKey]);

  const clearInvite = () => {
    storedInvite(null);
    try { sessionStorage.removeItem(returnKey); } catch (_) { /* Storage may be disabled. */ }
    const query = new URLSearchParams(location.search);
    query.delete("invite_token");
    const path = location.pathname === DDZ_INVITE_PATH ? "/home" : location.pathname;
    if (path !== location.pathname || query.toString() !== location.search.replace(/^\?/, "")) {
      history.replace(`${path}${query.toString() ? `?${query}` : ""}`);
    }
  };
  const open = game => {
    if (!isDDZEntry(game)) return false;
    clearInvite();
    if (!token) {
      setPersist("ddz_wallet_open", true);
      setPersist("login_back_url", ChannelRemind("/home"));
      history.push(ChannelRemind("/login"));
    } else {
      setEntry({ token, inviteToken: null, revision: ++revision.current });
    }
    return true;
  };

  const modal = token && entry && entry.token === token ? <DDZWalletEntry
    key={`${token}:${entry.revision}`} token={token} account={account} language={getLanguage()}
    inviteToken={entry.inviteToken} onClose={() => { setEntry(null); clearInvite(); }}
    onTransferred={() => reloadUserInfo(token)}
    onLaunch={url => {
      const current = getPersist("user_info");
      if (!current || current.token !== token) return;
      clearInvite();
      try { sessionStorage.setItem(returnKey, JSON.stringify({ inviteToken: null })); } catch (_) { /* Navigation still works. */ }
      window.location.assign(url);
    }}
  /> : null;
  return <EntryContext.Provider value={{ open, modal: null }}>{children}{modal}</EntryContext.Provider>;
}

export default function useDDZWalletEntry() {
  return useContext(EntryContext);
}
